/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* Licensed under the MIT License. See LICENSE.md in the project root for license terms.
*--------------------------------------------------------------------------------------------*/

import { CancellationToken, editor, languages, Position, Uri } from "monaco-editor/esm/vs/editor/editor.api";
import { Adapter } from "./Adapter";

export class DefinitionAdapter extends Adapter {
  public async provideDefinition(model: editor.ITextModel, position: Position, _token: CancellationToken): Promise<languages.Definition | undefined> {
    const resource = model.uri;
    const offset = model.getOffsetAt(position);
    const worker = await (await languages.typescript.getTypeScriptWorker())(resource);
    const entries = await worker.getDefinitionAtPosition(resource.toString(), offset);

    if (!entries || model.isDisposed()) {
      return;
    }

    if (model.isDisposed()) {
      return;
    }

    const result: languages.Location[] = [];
    for (const entry of entries) {
      const uri = Uri.parse(entry.fileName);
      const refModel = this.getOrCreateModel(uri, languages.typescript.typescriptDefaults.getExtraLibs());
      if (refModel) {
        result.push({
          uri,
          range: this._textSpanToRange(refModel, entry.textSpan)
        });
      }
    }
    return result;
  }

  private getOrCreateModel(uri: Uri, extraLibs: languages.typescript.IExtraLibs): editor.ITextModel | null {
    const model = editor.getModel(uri);
    if (model) {
      return model;
    }
    if (this.isLibFile(uri, extraLibs)) {
      return editor.createModel(extraLibs[uri.toString(true)].content, 'javascript', uri);
    }
    return null;
  }

  private isLibFile(uri: Uri | null, extraLibs: languages.typescript.IExtraLibs): boolean {
    if (!uri) {
      return false;
    }
    return !!extraLibs[uri.toString(true)];
  }
}